import { Project } from '../interfaces/project';

const STORAGE_PREFIX = 'portfolio.projectList.v1';
const TTL_MS = 24 * 60 * 60 * 1000;

export type ProjectListCacheScope = 'public' | 'signedIn';

interface StoredEnvelope {
  savedAt: number;
  projects: Project[];
}

function storageKey(scope: ProjectListCacheScope): string {
  return `${STORAGE_PREFIX}.${scope}`;
}

function reviveProject(raw: unknown): Project | null {
  if (!raw || typeof raw !== 'object') {
    return null;
  }
  const p = raw as Record<string, unknown>;
  const startDate =
    p['startDate'] != null ? new Date(p['startDate'] as string | number | Date) : undefined;
  const endDate = p['endDate'] != null ? new Date(p['endDate'] as string | number | Date) : undefined;
  return {
    ...(p as unknown as Project),
    startDate,
    endDate
  };
}

export function readProjectListCache(scope: ProjectListCacheScope): Project[] | null {
  try {
    const raw = localStorage.getItem(storageKey(scope));
    if (!raw) {
      return null;
    }
    const parsed = JSON.parse(raw) as StoredEnvelope;
    if (typeof parsed.savedAt !== 'number' || !Array.isArray(parsed.projects)) {
      return null;
    }
    if (Date.now() - parsed.savedAt > TTL_MS) {
      return null;
    }
    const projects: Project[] = [];
    for (const item of parsed.projects) {
      const proj = reviveProject(item);
      if (proj) {
        projects.push(proj);
      }
    }
    return projects.length > 0 ? projects : null;
  } catch {
    return null;
  }
}

export function writeProjectListCache(scope: ProjectListCacheScope, projects: Project[]): void {
  try {
    const envelope: StoredEnvelope = {
      savedAt: Date.now(),
      projects
    };
    localStorage.setItem(storageKey(scope), JSON.stringify(envelope));
  } catch {
    /* ignore quota / private mode */
  }
}
